import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { IconAlertTriangle, IconFilter, IconInfoCircle, IconListCheck, IconNote, IconSettings, IconUsers, IconX } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import { CommonAlert } from "src/components/alert/CommonAlert";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";
import { EligibilitySet, GetConditions, SetCondition, SetDocumentNote, SetDocumentRule, StandardSet } from "src/pages/list_documents/actions";
import { FormField, selectA11y } from "./FormBits";
import { confirmDiscard } from "./helpers";
import {
  STANDARD_GROUPS,
  ageAskedOf,
  ageConditionText,
  conditionFor,
  conditionText,
  dependOptions,
  hasBirthDate,
  matchChoices,
  readAgeRule,
  standardCounts,
  standardRow,
  standardSummary,
  whoIsAsked,
} from "./conditions";

// Who each document and question of a visa product is for, on the product's
// Documents and Questions screens: a line under each item ("Everyone", or
// "Only when Employment status is Employed") and a dialog to change it.
// Each change saves on its own (price/conditions/set), apart from the
// screen's Save button, which saves the list.

/** Staff who may edit products (the Countries privilege), as the price lists check it. */
export const canEditProducts = () => getLocalStorage(dataLocalStorage.privileges)?.["4"]?.access_edit == 1; // eslint-disable-line eqeqeq

/**
 * The product's conditions, read once: { status, questions, documents,
 * conditions, reload, saved }. status: loading | ready | notReady (the
 * server has no migration 014 yet) | failed.
 */
export function useConditions(priceId) {
  const dispatch = useDispatch();
  const [state, setState] = useState({ status: "loading", questions: [], documents: [], conditions: [], documentRules: false });

  const reload = useCallback(() => {
    if (!priceId) return;
    dispatch(
      GetConditions({
        price_id: priceId,
        callback: (result) => {
          if (!result.ok) {
            // An API from before conditions has no such route: nothing to set yet.
            setState((prev) => ({ ...prev, status: result.status === 404 ? "notReady" : "failed" }));
            return;
          }
          const data = result.data || {};
          setState({
            status: data.ready ? "ready" : "notReady",
            questions: data.questions || [],
            documents: data.documents || [],
            conditions: data.conditions || [],
            // A product's own rules on its documents can be set (migration 017).
            documentRules: Boolean(data.document_rules),
            // Who can apply by residence or a visa held (019), or null.
            eligibility: data.eligibility || null,
          });
        },
      })
    );
  }, [dispatch, priceId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // After a save: the item's condition as the server now has it (null: everyone).
  const saved = useCallback((itemType, itemId, condition) => {
    setState((prev) => ({
      ...prev,
      conditions: [
        ...prev.conditions.filter((row) => !(row.item_type === itemType && Number(row.item_id) === Number(itemId))),
        ...(condition ? [{ item_type: itemType, item_id: Number(itemId), ...condition }] : []),
      ],
    }));
  }, []);

  // After a note is saved: the document's note as the server now has it.
  const noted = useCallback((documentId, note) => {
    setState((prev) => ({
      ...prev,
      documents: prev.documents.map((doc) => (Number(doc.document_id) === Number(documentId) ? { ...doc, note } : doc)),
    }));
  }, []);

  // After a document rule is saved: the document as the server now reads it.
  const ruled = useCallback((documentId, data) => {
    setState((prev) => ({
      ...prev,
      documents: prev.documents.map((doc) =>
        Number(doc.document_id) === Number(documentId)
          ? { ...doc, rule: data.rule, required: data.required ?? doc.required, validity: data.validity, copy_kind: data.copy_kind }
          : doc
      ),
    }));
  }, []);

  return { ...state, reload, saved, noted, ruled };
}

/** Whether an item is on the product as the server has it (so it can take a condition). */
export const isOnProduct = (data, itemType, itemId) =>
  itemType === "document"
    ? data.documents.some((doc) => Number(doc.document_id) === Number(itemId))
    : data.questions.some((q) => Number(q.question_id) === Number(itemId));

/**
 * The one notice above the list when conditions can't be shown: the server
 * needs its update, or they didn't load.
 */
export const ConditionsNotice = ({ data }) => {
  if (data.status !== "notReady" && data.status !== "failed") return null;
  const failed = data.status === "failed";
  return (
    <Box
      sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5, px: 1.5, py: 1, borderRadius: 2, bgcolor: "action.hover", color: "text.secondary" }}
    >
      <Box component="span" aria-hidden sx={{ display: "flex", mt: "2px" }}>
        <IconInfoCircle size={18} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.primary">
          {failed
            ? "Who each item is for could not be loaded."
            : "Asking only some travellers needs a server update. Until then, everything here is asked of everyone."}
        </Typography>
        {failed ? (
          <ActionButton variant="text" onClick={data.reload} sx={{ ml: -1.5 }}>
            Try again
          </ActionButton>
        ) : null}
      </Box>
    </Box>
  );
};

ConditionsNotice.propTypes = { data: PropTypes.object.isRequired };

// The icon carries the colour; the words stay text-coloured, so the blue
// Change beside them is the only thing that looks pressable.
const TONES = {
  everyone: { icon: IconUsers, color: "text.secondary", text: "text.secondary" },
  some: { icon: IconFilter, color: "primary.main", text: "text.primary" },
  broken: { icon: IconAlertTriangle, color: "warning.dark", text: "warning.dark" },
};

/**
 * The line under one item: who it is for, and Change. `saved`: the item is
 * in the list as last saved (a newly added one must be saved first).
 */
export const WhoLine = ({ data, priceId, itemType, itemId, name, saved, canEdit, children }) => {
  const [open, setOpen] = useState(false);
  const changeRef = useRef(null);
  if (data.status !== "ready") return null;
  if (!saved) {
    return (
      <Typography component="span" variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
        Save the list first to choose who is asked.
      </Typography>
    );
  }
  // Saved but not on the product as the server reads it (switched off): not asked anyway.
  if (!isOnProduct(data, itemType, itemId)) return null;
  const who = whoIsAsked(conditionFor(data.conditions, itemType, itemId), data.questions);
  const tone = TONES[who.tone];
  const Icon = tone.icon;
  return (
    <Box component="span" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, mt: 0.5 }}>
      <Box component="span" sx={{ display: "inline-flex", alignItems: "flex-start", gap: 0.75, color: tone.color, minWidth: 0, py: 0.5 }}>
        <Box component="span" aria-hidden sx={{ display: "flex", mt: "2px", flexShrink: 0 }}>
          <Icon size={16} />
        </Box>
        <Typography component="span" variant="body2" sx={{ color: tone.text, fontWeight: who.tone === "everyone" ? 400 : 500 }}>
          <Box component="span" sx={visuallyHidden}>
            Who is asked:{" "}
          </Box>
          {who.text}
        </Typography>
      </Box>
      {canEdit ? (
        <ActionButton ref={changeRef} variant="text" onClick={() => setOpen(true)} aria-label={`Change who is asked for ${name}`} sx={{ ml: -1 }}>
          Change
        </ActionButton>
      ) : null}
      {children}
      {open ? (
        <WhoDialog
          data={data}
          priceId={priceId}
          itemType={itemType}
          itemId={itemId}
          name={name}
          onClose={() => {
            setOpen(false);
            setTimeout(() => changeRef.current?.focus(), 0);
          }}
        />
      ) : null}
    </Box>
  );
};

WhoLine.propTypes = {
  data: PropTypes.object.isRequired,
  priceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  itemType: PropTypes.oneOf(["document", "question"]).isRequired,
  itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string,
  saved: PropTypes.bool,
  canEdit: PropTypes.bool,
  // On the same line, after Change (the document's note).
  children: PropTypes.node,
};

// "1px", not 1: MUI reads 1 as 100%.
const visuallyHidden = { position: "absolute", width: "1px", height: "1px", overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" };

const lowerFirst = (sentence) => sentence.charAt(0).toLowerCase() + sentence.slice(1);

/** Everyone, or only when one of the product's choice questions has some answers. */
const WhoDialog = ({ data, priceId, itemType, itemId, name, onClose }) => {
  const dispatch = useDispatch();
  const alert = CommonAlert();
  const phone = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const options = dependOptions(data.questions, data.conditions, itemType, itemId);
  const current = conditionFor(data.conditions, itemType, itemId);
  const start = useRef(null);
  if (!start.current) {
    const currentQuestion = current ? options.find((q) => Number(q.question_id) === Number(current.when_question_id)) : null;
    start.current = {
      mode: current?.age ? "age" : current ? "some" : "everyone",
      questionId: currentQuestion ? String(currentQuestion.question_id) : options.length === 1 ? String(options[0].question_id) : "",
      values: currentQuestion ? matchChoices(current.values, currentQuestion.choices) : [],
      under: current?.age?.under != null ? String(current.age.under) : "",
      atLeast: current?.age?.at_least != null ? String(current.age.at_least) : "",
    };
  }
  const [mode, setMode] = useState(start.current.mode);
  const [questionId, setQuestionId] = useState(start.current.questionId);
  const [values, setValues] = useState(start.current.values);
  const [under, setUnder] = useState(start.current.under);
  const [atLeast, setAtLeast] = useState(start.current.atLeast);
  // By age needs the product's date of birth (counted on arrival).
  const byAgePossible = hasBirthDate(data.questions);
  const ageRule = mode === "age" ? readAgeRule({ under, atLeast }) : null;
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const question = options.find((q) => String(q.question_id) === questionId) || null;
  const noun = itemType === "document" ? "document" : "question";
  const noOptions = options.length === 0;
  const ids = {
    question: `who-${itemType}-${itemId}-question`,
    values: `who-${itemType}-${itemId}-values`,
    under: `who-${itemType}-${itemId}-under`,
    atLeast: `who-${itemType}-${itemId}-at-least`,
  };

  const changed =
    mode !== start.current.mode ||
    (mode === "some" && (questionId !== start.current.questionId || values.join("\u0000") !== start.current.values.join("\u0000"))) ||
    (mode === "age" && (under !== start.current.under || atLeast !== start.current.atLeast));

  const close = () => {
    if (!changed) return onClose();
    return confirmDiscard(alert, { title: "Discard this change?", message: `Who is asked for ${name} stays as it was.`, onDiscard: onClose });
  };

  const pickQuestion = (event) => {
    setQuestionId(event.target.value);
    setValues([]);
    setErrors({});
  };

  // Ticked answers stay in the question's own order.
  const toggle = (choice) => {
    setErrors((prev) => ({ ...prev, values: undefined }));
    setValues((prev) => {
      const next = prev.includes(choice) ? prev.filter((value) => value !== choice) : [...prev, choice];
      return (question?.choices || []).filter((entry) => next.includes(entry));
    });
  };

  const saveAge = () => {
    if (ageRule.errors) {
      setErrors(ageRule.errors);
      setTimeout(() => document.getElementById(ageRule.errors.under ? ids.under : ids.atLeast)?.focus(), 0);
      return undefined;
    }
    setSaveError("");
    return new Promise((resolve) => {
      dispatch(
        SetCondition({
          price_id: priceId,
          item_type: itemType,
          item_id: itemId,
          age: ageRule.payload,
          callback: (result) => {
            resolve();
            if (!result.ok) {
              setSaveError(result.message || "This wasn't saved. Please try again.");
              return;
            }
            const saved = result.data?.condition;
            data.saved(itemType, itemId, { when_question_id: Number(saved?.when_question_id), values: [], age: saved?.age || ageRule.age });
            toast.success(`Saved. ${name} is asked ${ageAskedOf(ageRule.age)}.`);
            onClose();
          },
        })
      );
    });
  };

  const save = () => {
    if (mode === "age") return saveAge();
    const some = mode === "some";
    if (some) {
      const found = {};
      if (!question) found.question = noOptions ? "There is no question left to depend on. Choose Everyone." : "Choose the question it depends on.";
      else if (!values.length) found.values = "Tick at least one answer.";
      if (found.question || found.values) {
        setErrors(found);
        setTimeout(() => {
          const target = found.question
            ? document.getElementById(ids.question)
            : document.querySelector(`#${ids.values} input[type="checkbox"]`);
          target?.focus();
        }, 0);
        return undefined;
      }
    }
    setSaveError("");
    return new Promise((resolve) => {
      dispatch(
        SetCondition({
          price_id: priceId,
          item_type: itemType,
          item_id: itemId,
          when_question_id: some ? question.question_id : null,
          values: some ? values : [],
          callback: (result) => {
            resolve();
            if (!result.ok) {
              setSaveError(result.message || "This wasn't saved. Please try again.");
              return;
            }
            const condition = some ? { when_question_id: Number(question.question_id), values } : null;
            data.saved(itemType, itemId, condition);
            toast.success(
              some
                ? `Saved. ${name} is asked ${lowerFirst(conditionText(question.label, values))}.`
                : `Saved. ${name} is asked of everyone.`
            );
            onClose();
          },
        })
      );
    });
  };

  const selectProps = selectA11y(ids.question, { hint: true, error: errors.question });

  return (
    <Dialog open onClose={close} fullScreen={phone} maxWidth="sm" fullWidth aria-labelledby="who-dialog-title">
      <DialogTitle id="who-dialog-title" sx={{ pr: 7 }}>
        Who is asked for this {noun}
        <Typography variant="body2" color="text.secondary">
          {name}
        </Typography>
        <IconButton onClick={close} aria-label="Close" sx={{ position: "absolute", right: 12, top: 12 }}>
          <IconX size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <RadioGroup
            value={mode}
            onChange={(event) => {
              setMode(event.target.value);
              setErrors({});
            }}
            aria-label={`Who is asked for ${name}`}
          >
            <FormControlLabel
              value="everyone"
              control={<Radio />}
              label={
                <Box sx={{ py: 0.75 }}>
                  <Typography>Everyone</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Every traveller on this product.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="some"
              disabled={noOptions && mode !== "some"}
              control={<Radio />}
              label={
                <Box sx={{ py: 0.75 }}>
                  <Typography>Only some travellers</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {noOptions
                      ? `No ${itemType === "question" ? "other " : ""}question on this product has set answers to depend on. Add one under Questions first.`
                      : "Depends on their answer to one of this product's questions."}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="age"
              disabled={!byAgePossible && mode !== "age"}
              control={<Radio />}
              label={
                <Box sx={{ py: 0.75 }}>
                  <Typography>By age on arrival</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {byAgePossible
                      ? "Counted on the arrival date, from the date of birth. For example, a child’s birth certificate under 18."
                      : "This product doesn’t ask for a date of birth. Add that question first."}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>

          {mode === "age" ? (
            <Stack spacing={2.5} sx={{ pl: { sm: 4 } }}>
              <FormField id={ids.under} label="Younger than" optional hint="18 asks travellers under 18 on arrival." error={errors.under}>
                <TextField
                  id={ids.under}
                  value={under}
                  onChange={(event) => {
                    setUnder(event.target.value.replace(/[^0-9]/g, "").slice(0, 3));
                    setErrors({});
                  }}
                  inputProps={{ inputMode: "numeric", "aria-describedby": [`${ids.under}-hint`, errors.under ? `${ids.under}-error` : null].filter(Boolean).join(" ") }}
                  error={Boolean(errors.under)}
                  sx={{ maxWidth: 160 }}
                />
              </FormField>
              <FormField id={ids.atLeast} label="From age" optional hint="65 asks travellers 65 or older on arrival." error={errors.atLeast}>
                <TextField
                  id={ids.atLeast}
                  value={atLeast}
                  onChange={(event) => {
                    setAtLeast(event.target.value.replace(/[^0-9]/g, "").slice(0, 3));
                    setErrors({});
                  }}
                  inputProps={{ inputMode: "numeric", "aria-describedby": [`${ids.atLeast}-hint`, errors.atLeast ? `${ids.atLeast}-error` : null].filter(Boolean).join(" ") }}
                  error={Boolean(errors.atLeast)}
                  sx={{ maxWidth: 160 }}
                />
              </FormField>
              {ageRule?.age ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", borderRadius: 2, bgcolor: "primary.light", px: 1.5, py: 1.25 }} aria-live="polite">
                  <Box component="span" aria-hidden sx={{ display: "flex", mt: "2px", color: "primary.main" }}>
                    <IconFilter size={16} />
                  </Box>
                  <Typography variant="body2">{ageConditionText(ageRule.age)} are asked.</Typography>
                </Box>
              ) : null}
            </Stack>
          ) : null}

          {mode === "some" && noOptions ? (
            <Typography id={ids.question} tabIndex={-1} role="alert" variant="body2" color="error.main" sx={{ pl: { sm: 4 }, outline: "none" }}>
              There is no question left to depend on. Choose Everyone.
            </Typography>
          ) : null}

          {mode === "some" && !noOptions ? (
            <Stack spacing={2.5} sx={{ pl: { sm: 4 } }}>
              <FormField id={ids.question} label="It depends on" hint="Only questions with set answers are listed." error={errors.question}>
                <TextField
                  select
                  fullWidth
                  value={questionId}
                  onChange={pickQuestion}
                  error={Boolean(errors.question)}
                  SelectProps={{
                    ...selectProps,
                    SelectDisplayProps: { ...selectProps.SelectDisplayProps, id: ids.question },
                    renderValue: (value) =>
                      value ? (
                        options.find((q) => String(q.question_id) === value)?.label
                      ) : (
                        <Typography component="span" color="text.secondary">
                          Choose a question
                        </Typography>
                      ),
                  }}
                >
                  {options.map((q) => (
                    <MenuItem key={q.question_id} value={String(q.question_id)} sx={{ whiteSpace: "normal" }}>
                      <Box>
                        <Typography>{q.label}</Typography>
                        {q.section ? (
                          <Typography variant="caption" color="text.secondary">
                            {q.section}
                          </Typography>
                        ) : null}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </FormField>

              {question ? (
                <Box component="fieldset" id={ids.values} aria-describedby={errors.values ? `${ids.values}-error` : undefined} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
                  <Typography component="legend" variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, p: 0 }}>
                    Asked when the answer is
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Tick one or more.
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: question.choices.length > 6 ? "1fr 1fr" : "1fr" },
                      columnGap: 2,
                      maxHeight: 320,
                      overflowY: "auto",
                    }}
                  >
                    {question.choices.map((choice) => (
                      <FormControlLabel
                        key={choice}
                        control={<Checkbox checked={values.includes(choice)} onChange={() => toggle(choice)} />}
                        label={choice}
                        sx={{ mr: 0, minHeight: 44 }}
                      />
                    ))}
                  </Box>
                  {errors.values ? (
                    <Typography id={`${ids.values}-error`} role="alert" variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                      {errors.values}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}

              {question && values.length ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", borderRadius: 2, bgcolor: "primary.light", px: 1.5, py: 1.25 }} aria-live="polite">
                  <Box component="span" aria-hidden sx={{ display: "flex", mt: "2px", color: "primary.main" }}>
                    <IconFilter size={16} />
                  </Box>
                  <Typography variant="body2">Travellers are asked {lowerFirst(conditionText(question.label, values))}.</Typography>
                </Box>
              ) : null}
            </Stack>
          ) : null}

          {saveError ? (
            <Alert severity="error" role="alert">
              {saveError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
          <ActionButton variant="text" size="medium" onClick={close}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" size="medium" busyLabel="Saving…" onClick={save}>
            Save
          </ActionButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

WhoDialog.propTypes = {
  data: PropTypes.object.isRequired,
  priceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  itemType: PropTypes.oneOf(["document", "question"]).isRequired,
  itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

// ---- this product's rules on a document (migration 017) -----------------------

/** "valid 3 months after leaving", "valid for the whole stay", or null. */
export function validityText(validity) {
  if (!validity) return null;
  const months = Number(validity.months) || 0;
  if (validity.from === "departure") return months ? `valid ${months} month${months === 1 ? "" : "s"} after leaving` : "valid for the whole stay";
  return `valid ${months} month${months === 1 ? "" : "s"} after arrival`;
}

export const COPY_TEXT = { scan: "a scan or photo", original: "the original, at the appointment", both: "a scan now, and the original at the appointment" };

/** What this product says of the document, over its own settings, in a line; null when nothing. */
export function ruleSummary(doc) {
  const rule = doc?.rule;
  if (!rule) return null;
  const parts = [];
  if (rule.required === true) parts.push("required");
  if (rule.required === false) parts.push("optional");
  if (rule.valid_months !== null && rule.valid_months !== undefined) parts.push(validityText(doc.validity) || "no validity rule");
  if (rule.copy_kind) parts.push(COPY_TEXT[rule.copy_kind]);
  return parts.length ? parts.join(" · ") : null;
}

// The dialog's validity choice, from the rule ("" keeps the document's own).
function validityChoice(rule) {
  if (!rule || rule.valid_months === null || rule.valid_months === undefined) return { kind: "", months: "" };
  const months = Number(rule.valid_months) || 0;
  if (rule.valid_from === "departure") return months ? { kind: "departure", months } : { kind: "stay", months: "" };
  return months ? { kind: "arrival", months } : { kind: "none", months: "" };
}

/** The validity choice as the API takes it. */
export function validityRule(kind, months) {
  if (kind === "none") return { valid_months: 0, valid_from: "arrival" };
  if (kind === "stay") return { valid_months: 0, valid_from: "departure" };
  if (kind === "arrival" || kind === "departure") return { valid_months: Number(months), valid_from: kind };
  return { valid_months: null, valid_from: null };
}

/**
 * What this product asks of a document over its shared settings: required
 * or optional here, how long it must still be valid (after arrival, after
 * leaving, or for the whole stay), and whether a scan or the original is
 * needed. Goes inside the document's WhoLine, after its note.
 */
export const RuleLine = ({ data, priceId, documentId, name, canEdit }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  if (data.status !== "ready" || !data.documentRules) return null;
  const doc = data.documents.find((d) => Number(d.document_id) === Number(documentId));
  if (!doc) return null;
  const summary = ruleSummary(doc);
  if (!summary && !canEdit) return null;
  return (
    <Box component="span" sx={summary ? { display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, flexBasis: "100%" } : { display: "inline-flex" }}>
      {summary ? (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "flex-start", gap: 0.75, minWidth: 0, py: 0.5 }}>
          <Box component="span" aria-hidden sx={{ display: "flex", mt: "2px", flexShrink: 0, color: "text.secondary" }}>
            <IconSettings size={16} />
          </Box>
          <Typography component="span" variant="body2" color="text.primary">
            <Box component="span" sx={{ fontWeight: 600 }}>
              Rules for this visa:
            </Box>{" "}
            {summary}
          </Typography>
        </Box>
      ) : null}
      {canEdit ? (
        <ActionButton
          ref={buttonRef}
          variant="text"
          onClick={() => setOpen(true)}
          aria-label={summary ? `Edit rules for ${name} on this visa` : `Rules for this visa: set them for ${name}`}
          sx={{ ml: -1 }}
        >
          {summary ? "Edit rules" : "Rules for this visa"}
        </ActionButton>
      ) : null}
      {open ? (
        <RuleDialog
          data={data}
          priceId={priceId}
          doc={doc}
          name={name}
          onClose={() => {
            setOpen(false);
            setTimeout(() => buttonRef.current?.focus(), 0);
          }}
        />
      ) : null}
    </Box>
  );
};

RuleLine.propTypes = {
  data: PropTypes.object.isRequired,
  priceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string,
  canEdit: PropTypes.bool,
};

const RuleDialog = ({ data, priceId, doc, name, onClose }) => {
  const dispatch = useDispatch();
  const alert = CommonAlert();
  const phone = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const start = {
    required: doc.rule?.required === true ? "yes" : doc.rule?.required === false ? "no" : "",
    ...validityChoice(doc.rule),
    copy: doc.rule?.copy_kind || "",
  };
  const [form, setForm] = useState(start);
  const [saveError, setSaveError] = useState("");
  const [tried, setTried] = useState(false);
  const id = `rule-${doc.document_id}`;
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const changed = JSON.stringify(form) !== JSON.stringify(start);
  const needsMonths = form.kind === "arrival" || form.kind === "departure";
  const monthsError =
    needsMonths && !(Number.isInteger(Number(form.months)) && Number(form.months) >= 1 && Number(form.months) <= 120 && String(form.months).trim() !== "")
      ? "Enter the months, from 1 to 120."
      : null;
  const ownRequired = doc.own?.required ? "Required" : "Optional";
  const ownValidity = doc.own?.valid_months ? `${doc.own.valid_months} month${doc.own.valid_months === 1 ? "" : "s"} after arrival` : "no validity rule";

  const close = () => {
    if (!changed) return onClose();
    return confirmDiscard(alert, { title: "Discard these rules?", message: `The rules for ${name} stay as they were.`, onDiscard: onClose });
  };

  const save = () => {
    setTried(true);
    if (monthsError) {
      document.getElementById(`${id}-months`)?.focus();
      return undefined;
    }
    setSaveError("");
    const rule = {
      required: form.required === "yes" ? true : form.required === "no" ? false : null,
      ...validityRule(form.kind, form.months),
      copy_kind: form.copy || null,
    };
    return new Promise((resolve) => {
      dispatch(
        SetDocumentRule({
          price_id: priceId,
          document_id: doc.document_id,
          rule,
          callback: (result) => {
            resolve();
            if (!result.ok) {
              setSaveError(result.status === 409 ? "These rules need a server update first (migration 017)." : result.message || "This wasn't saved. Please try again.");
              return;
            }
            data.ruled(doc.document_id, result.data || {});
            toast.success(`Saved. Travellers see the rules for ${name} on this visa.`);
            onClose();
          },
        })
      );
    });
  };

  return (
    <Dialog open onClose={close} fullScreen={phone} maxWidth="sm" fullWidth aria-labelledby={`${id}-title`}>
      <DialogTitle id={`${id}-title`} sx={{ pr: 7 }}>
        Rules for this visa
        <Typography variant="body2" color="text.secondary">
          {name}: only for this product. The document’s own settings stay for the others.
        </Typography>
        <IconButton onClick={close} aria-label="Close" sx={{ position: "absolute", right: 12, top: 12 }}>
          <IconX size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <FormField id={`${id}-required`} label="Required for this visa">
            <RadioGroup
              aria-labelledby={`${id}-required-label`}
              value={form.required}
              onChange={(event) => set({ required: event.target.value })}
            >
              <FormControlLabel value="" control={<Radio />} label={`As the document says (${ownRequired})`} />
              <FormControlLabel value="yes" control={<Radio />} label="Required" />
              <FormControlLabel value="no" control={<Radio />} label="Optional" />
            </RadioGroup>
          </FormField>
          <FormField
            id={`${id}-validity`}
            label="How long it must still be valid"
            hint="Schengen passports: 3 months after leaving. UK: for the whole stay. Travellers also see the document’s description: check it doesn’t say otherwise."
          >
            <TextField
              id={`${id}-validity`}
              select
              fullWidth
              value={form.kind}
              onChange={(event) => set({ kind: event.target.value })}
              SelectProps={selectA11y(`${id}-validity`, { hint: true })}
            >
              <MenuItem value="">As the document says ({ownValidity})</MenuItem>
              <MenuItem value="arrival">Months after arrival</MenuItem>
              <MenuItem value="departure">Months after leaving</MenuItem>
              <MenuItem value="stay">For the whole stay</MenuItem>
              <MenuItem value="none">No validity rule for this visa</MenuItem>
            </TextField>
          </FormField>
          {needsMonths ? (
            <FormField id={`${id}-months`} label="Months" error={tried ? monthsError : undefined}>
              <TextField
                id={`${id}-months`}
                type="number"
                value={form.months}
                onChange={(event) => set({ months: event.target.value })}
                error={tried && Boolean(monthsError)}
                inputProps={{ min: 1, max: 120, inputMode: "numeric" }}
                sx={{ maxWidth: 160 }}
              />
            </FormField>
          ) : null}
          <FormField id={`${id}-copy`} label="What travellers give" hint="Shown to travellers under the document.">
            <TextField
              id={`${id}-copy`}
              select
              fullWidth
              value={form.copy}
              onChange={(event) => set({ copy: event.target.value })}
              SelectProps={selectA11y(`${id}-copy`, { hint: true })}
            >
              <MenuItem value="">Not stated by the embassy</MenuItem>
              <MenuItem value="scan">A scan or photo</MenuItem>
              <MenuItem value="original">The original, at the appointment</MenuItem>
              <MenuItem value="both">A scan now, and the original at the appointment</MenuItem>
            </TextField>
          </FormField>
          {saveError ? (
            <Alert severity="error" role="alert">
              {saveError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
          <ActionButton variant="text" size="medium" onClick={close}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" size="medium" busyLabel="Saving…" onClick={save} disabled={!changed}>
            Save
          </ActionButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

RuleDialog.propTypes = {
  data: PropTypes.object.isRequired,
  priceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  doc: PropTypes.object.isRequired,
  name: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

// ---- this product's note on a document ----------------------------------------

/**
 * What this product's embassy asks of a document beyond its description
 * (the balance a bank statement must show, how the invitation letter is
 * written): shown to travellers under the document. Without one, "Add a
 * note" beside Change; with one, the note on its own line and Edit. Goes
 * inside the document's WhoLine.
 */
export const NoteLine = ({ data, priceId, documentId, name, canEdit }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  if (data.status !== "ready") return null;
  const doc = data.documents.find((d) => Number(d.document_id) === Number(documentId));
  if (!doc) return null;
  const note = doc.note || "";
  if (!note && !canEdit) return null;
  return (
    <Box
      component="span"
      sx={note ? { display: "flex", alignItems: "flex-start", flexWrap: "wrap", columnGap: 1, flexBasis: "100%" } : { display: "inline-flex" }}
    >
      {note ? (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "flex-start", gap: 0.75, minWidth: 0, py: 0.5 }}>
          <Box component="span" aria-hidden sx={{ display: "flex", mt: "2px", flexShrink: 0, color: "text.secondary" }}>
            <IconNote size={16} />
          </Box>
          <Typography component="span" variant="body2" color="text.primary" sx={{ whiteSpace: "pre-line" }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              For this visa:
            </Box>{" "}
            {note}
          </Typography>
        </Box>
      ) : null}
      {canEdit ? (
        <ActionButton
          ref={buttonRef}
          variant="text"
          onClick={() => setOpen(true)}
          aria-label={note ? `Edit note on ${name}` : `Add a note on ${name} for this visa`}
          sx={{ ml: -1 }}
        >
          {note ? "Edit note" : "Add a note"}
        </ActionButton>
      ) : null}
      {open ? (
        <NoteDialog
          data={data}
          priceId={priceId}
          documentId={documentId}
          name={name}
          note={note}
          onClose={() => {
            setOpen(false);
            setTimeout(() => buttonRef.current?.focus(), 0);
          }}
        />
      ) : null}
    </Box>
  );
};

NoteLine.propTypes = {
  data: PropTypes.object.isRequired,
  priceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string,
  canEdit: PropTypes.bool,
};

const NOTE_MAX = 1000;

const NoteDialog = ({ data, priceId, documentId, name, note, onClose }) => {
  const dispatch = useDispatch();
  const alert = CommonAlert();
  const phone = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [value, setValue] = useState(note);
  const [saveError, setSaveError] = useState("");
  const id = `note-${documentId}`;
  const changed = value.trim() !== note.trim();
  const tooLong = value.trim().length > NOTE_MAX;

  const close = () => {
    if (!changed) return onClose();
    return confirmDiscard(alert, { title: "Discard this note?", message: `The note on ${name} stays as it was.`, onDiscard: onClose });
  };

  const save = () => {
    if (tooLong) {
      document.getElementById(id)?.focus();
      return undefined;
    }
    setSaveError("");
    return new Promise((resolve) => {
      dispatch(
        SetDocumentNote({
          price_id: priceId,
          document_id: documentId,
          note: value.trim(),
          callback: (result) => {
            resolve();
            if (!result.ok) {
              setSaveError(result.status === 409 ? "Notes need a server update first." : result.message || "This wasn't saved. Please try again.");
              return;
            }
            data.noted(documentId, result.data?.note || null);
            toast.success(result.data?.note ? `Saved. Travellers see the note on ${name}.` : `Note removed from ${name}.`);
            onClose();
          },
        })
      );
    });
  };

  return (
    <Dialog open onClose={close} fullScreen={phone} maxWidth="sm" fullWidth aria-labelledby={`${id}-title`}>
      <DialogTitle id={`${id}-title`} sx={{ pr: 7 }}>
        Note for this visa
        <Typography variant="body2" color="text.secondary">
          {name}
        </Typography>
        <IconButton onClick={close} aria-label="Close" sx={{ position: "absolute", right: 12, top: 12 }}>
          <IconX size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FormField
            id={id}
            label="What this embassy asks of it"
            hint="Only for this product. Travellers see it under the document, after “For this visa:”. Empty removes it."
            error={tooLong ? `Keep it under ${NOTE_MAX.toLocaleString("en")} characters.` : undefined}
          >
            <TextField
              id={id}
              fullWidth
              multiline
              minRows={3}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              error={tooLong}
              placeholder="A balance of at least … over the last 6 months, stamped by the bank."
              inputProps={{ "aria-describedby": tooLong ? `${id}-hint ${id}-error` : `${id}-hint` }}
            />
          </FormField>
          {saveError ? (
            <Alert severity="error" role="alert">
              {saveError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
          <ActionButton variant="text" size="medium" onClick={close}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" size="medium" busyLabel="Saving…" onClick={save}>
            Save
          </ActionButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

NoteDialog.propTypes = {
  data: PropTypes.object.isRequired,
  priceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string,
  note: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

// ---- the standard set --------------------------------------------------------

/**
 * "Add the standard set": a preview of what it adds to this product (by
 * work, how the trip is paid, guarantor, invitation), then add. onAdded:
 * reload the screen's list.
 */
/** "Add the questions for who can apply": shown when the product has a "Who can apply" by residence or visa (019). */
export const EligibilitySetButton = ({ data, priceId, onAdded }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  if (data.status !== "ready" || !data.eligibility) return null;
  return (
    <>
      <ActionButton ref={buttonRef} variant="outlined" startIcon={<IconUsers size={18} />} onClick={() => setOpen(true)}>
        Add the questions for who can apply
      </ActionButton>
      {open ? (
        <StandardSetDialog
          kind="eligibility"
          priceId={priceId}
          onAdded={() => {
            data.reload();
            onAdded?.();
          }}
          onClose={() => {
            setOpen(false);
            setTimeout(() => buttonRef.current?.focus(), 0);
          }}
        />
      ) : null}
    </>
  );
};

EligibilitySetButton.propTypes = {
  data: PropTypes.object.isRequired,
  priceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onAdded: PropTypes.func,
};

export const StandardSetButton = ({ data, priceId, onAdded }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  if (data.status !== "ready") return null;
  return (
    <>
      <ActionButton ref={buttonRef} variant="outlined" startIcon={<IconListCheck size={18} />} onClick={() => setOpen(true)}>
        Add the standard set
      </ActionButton>
      {open ? (
        <StandardSetDialog
          priceId={priceId}
          onAdded={() => {
            data.reload();
            onAdded?.();
          }}
          onClose={() => {
            setOpen(false);
            setTimeout(() => buttonRef.current?.focus(), 0);
          }}
        />
      ) : null}
    </>
  );
};

StandardSetButton.propTypes = {
  data: PropTypes.object.isRequired,
  priceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onAdded: PropTypes.func,
};

const STATE_COLOR = { new: "primary.main", add: "primary.main", have: "text.secondary", left: "warning.dark" };

const PreviewRow = ({ entry }) => {
  const row = standardRow(entry);
  return (
    <Box component="li" sx={{ py: 1, borderRadius: 0, borderBottom: 1, borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {row.name}
      </Typography>
      <Typography variant="body2" sx={{ color: STATE_COLOR[row.state] }}>
        {row.text}
      </Typography>
      {row.who ? (
        <Typography variant="body2" color="text.secondary">
          {row.who}
        </Typography>
      ) : null}
      {row.note ? (
        <Typography variant="body2" color="warning.dark">
          {row.note}
        </Typography>
      ) : null}
    </Box>
  );
};

PreviewRow.propTypes = { entry: PropTypes.object.isRequired };

const PreviewGroup = ({ title, entries }) =>
  entries.length ? (
    <Box>
      <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {entries.map((entry) => (
          <PreviewRow key={entry.id || entry.key} entry={entry} />
        ))}
      </Box>
    </Box>
  ) : null;

PreviewGroup.propTypes = { title: PropTypes.string.isRequired, entries: PropTypes.array.isRequired };

// The two sets a product can take: the standard one, and what its "Who can
// apply" needs (the questions it goes by, and the residence permit or visa
// copy asked of those it applies to).
const SETS = {
  standard: {
    action: StandardSet,
    title: "Add the standard set",
    lead: "The questions and documents travellers from Lebanon are usually asked, each for the travellers it fits (by work, who pays, who invites). After adding it, remove what this embassy doesn't ask, and add notes for what it asks beyond them.",
    loadFailed: "The standard set couldn't be loaded.",
    saveFailed: "The standard set wasn’t added. Nothing was changed.",
    done: (added) => `Standard set added: ${added.questions ?? 0} questions, ${added.documents ?? 0} documents, ${added.conditions ?? 0} rules.`,
    groups: STANDARD_GROUPS,
  },
  eligibility: {
    action: EligibilitySet,
    title: "Add the questions for who can apply",
    lead: "Where they live and which visa they hold, asked of every traveller, so the website can tell who can apply. A residence permit is asked of those who live in one of the countries, and a copy of the visa of those who hold one.",
    loadFailed: "The questions for who can apply couldn't be loaded.",
    saveFailed: "They weren’t added. Nothing was changed.",
    done: (added) => `Added: ${added.questions ?? 0} questions, ${added.documents ?? 0} documents, ${added.conditions ?? 0} rules.`,
    groups: [{ key: "eligibility", title: "Who can apply" }],
  },
};

const StandardSetDialog = ({ priceId, onAdded, onClose, kind = "standard" }) => {
  const set = SETS[kind];
  const dispatch = useDispatch();
  const phone = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [plan, setPlan] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");

  const load = useCallback(() => {
    setLoadError("");
    dispatch(
      set.action({
        price_id: priceId,
        apply: false,
        callback: (result) => (result.ok ? setPlan(result.data) : setLoadError(result.message || set.loadFailed)),
      })
    );
  }, [dispatch, priceId, set]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = plan ? standardCounts(plan) : null;
  const nothing = counts && !counts.questions && !counts.documents;

  const add = () => {
    setSaveError("");
    return new Promise((resolve) => {
      dispatch(
        set.action({
          price_id: priceId,
          apply: true,
          callback: (result) => {
            resolve();
            if (!result.ok) {
              // Always that nothing changed; the server's reason after it.
              setSaveError(`${set.saveFailed}${result.message ? ` ${result.message}` : ""}`);
              return;
            }
            const added = result.data?.added || {};
            toast.success(set.done(added));
            onAdded();
            onClose();
          },
        })
      );
    });
  };

  return (
    <Dialog open onClose={onClose} fullScreen={phone} maxWidth="md" fullWidth aria-labelledby={`${kind}-set-title`}>
      <DialogTitle id={`${kind}-set-title`} sx={{ pr: 7 }}>
        {set.title}
        <Typography variant="body2" color="text.secondary">
          {set.lead}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close" sx={{ position: "absolute", right: 12, top: 12 }}>
          <IconX size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loadError ? (
          <Stack spacing={1} alignItems="flex-start">
            <Alert severity="error" role="alert" sx={{ width: "100%" }}>
              {loadError}
            </Alert>
            <ActionButton variant="text" onClick={load}>
              Try again
            </ActionButton>
          </Stack>
        ) : !plan ? (
          <Typography color="text.secondary" role="status">
            Checking this product…
          </Typography>
        ) : (
          <Stack spacing={3}>
            <Typography sx={{ fontWeight: 600 }} role="status">
              {standardSummary(counts)}
            </Typography>
            <PreviewGroup title="Questions" entries={plan.questions.filter((entry) => entry.action !== "not_asked")} />
            {set.groups.map((group) => (
              <PreviewGroup key={group.key} title={`Documents · ${group.title}`} entries={plan.documents.filter((entry) => entry.group === group.key)} />
            ))}
          </Stack>
        )}
      </DialogContent>
      {/* A failed add is said next to the button that was pressed, not at
          the foot of a long list nobody scrolled to (CMS review sami-13). */}
      <DialogActions disableSpacing sx={{ px: 3, py: 2, gap: 1.5, flexDirection: "column", alignItems: { xs: "stretch", sm: "flex-end" } }}>
        {saveError ? (
          <Alert severity="error" role="alert" sx={{ width: "100%" }}>
            {saveError}
          </Alert>
        ) : null}
        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
          <ActionButton variant="text" size="medium" onClick={onClose}>
            {nothing ? "Close" : "Cancel"}
          </ActionButton>
          {plan && !nothing ? (
            <ActionButton variant="contained" size="medium" busyLabel="Adding…" onClick={add}>
              Add to this product
            </ActionButton>
          ) : null}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

StandardSetDialog.propTypes = {
  priceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onAdded: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  kind: PropTypes.oneOf(["standard", "eligibility"]),
};
